import { type IconType } from '../objects/IconsHelper';
import { defaultShapeProps, normalizeSpacing } from '@lib/canvasx/shapes';
import { Icon, measureIcon } from './Icon';

type SquareIconProps = Partial<ShapeStruct & ScaleStruct & DimensionStruct> & {
  icon: IconType;
  size: number;
  iconFill?: CanvasFillStrokeStyles['fillStyle'];
};

export const SquareIcon: CanvaSXComponent<
  SquareIconProps,
  typeof defaultShapeProps
> = function (props) {
  const { size } = props;

  const { width, height } = measureIcon(props);
  const padding = normalizeSpacing(props.padding);

  // We want to render the icon as a square of the specified size
  // We need to remove padding then calculate the scale of the icon, then add H/V padding to adjust the alignment of the icon
  const availableWidth = size - padding.left - padding.right;
  const availableHeight = size - padding.top - padding.bottom;

  const horizontalScale = availableWidth / width;
  const verticalScale = availableHeight / height;

  const scale = Math.min(horizontalScale, verticalScale);

  const horizontalPadding = (size - width * scale) / 2;
  const verticalPadding = (size - height * scale) / 2;

  const newPadding: SpacingSidesStruct = {
    left: padding.left + horizontalPadding,
    right: padding.right + horizontalPadding,
    top: padding.top + verticalPadding,
    bottom: padding.bottom + verticalPadding,
  };

  Icon({
    ...props,
    scale,
    padding: newPadding,
  });

  return null;
};

SquareIcon.calculateDimensions = props => {
  const margin = normalizeSpacing(props.margin);
  const padding = normalizeSpacing(props.padding);

  return {
    width:
      margin.left + margin.right + padding.left + padding.right + props.size,
    height:
      margin.top + margin.bottom + padding.top + padding.bottom + props.size,
  };
};

SquareIcon.defaultProps = { ...defaultShapeProps };
